/**
 * Resolve a media URL into an absolute one.
 * - Returns absolute URLs as-is.
 * - Prepends the backend origin to relative `/api/uploads/...` paths.
 * - Returns "" for falsy input.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return `${BACKEND}${url}`;
  return url;
};

// Match a Cloudinary "/upload/..." URL — capture the segment we can inject
// transformation params into: everything after "/upload/" and before "vNNN/".
const CLOUDINARY_UPLOAD_RE =
  /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(?:([^/]+)\/)?(v\d+\/.+)$/;

const isUnsplashUrl = (url) => /^https?:\/\/images\.unsplash\.com\//i.test(url);

/**
 * Return a variant of the given URL at the requested width, with automatic
 * quality + format (WebP/AVIF where supported). Supports:
 *   - Cloudinary "/image/upload/" URLs (prepends c_limit,w_X,q_auto,f_auto)
 *   - Unsplash URLs (rewrites the `w` query param)
 *   - Any other URL → returned unchanged.
 */
export const responsiveVariant = (url, width) => {
  if (!url || !width) return url || "";
  const absolute = resolveMediaUrl(url);
  const m = absolute.match(CLOUDINARY_UPLOAD_RE);
  if (m) {
    const t = `c_limit,w_${width},q_auto,f_auto`;
    const existing = m[2] ? `${m[2]}/` : "";
    return `${m[1]}${t}/${existing}${m[3]}`;
  }
  if (isUnsplashUrl(absolute)) {
    try {
      const u = new URL(absolute);
      u.searchParams.set("w", String(width));
      // Nudge quality lower for very small variants to save bytes.
      if (width <= 480 && !u.searchParams.get("q")) {
        u.searchParams.set("q", "70");
      }
      return u.toString();
    } catch {
      return absolute;
    }
  }
  return absolute;
};

/**
 * Build a srcSet string for the given URL at multiple widths.
 * Returns "" for URLs where variants can't be generated.
 */
export const responsiveSrcSet = (url, widths = [320, 640, 960, 1280]) => {
  if (!url) return "";
  const absolute = resolveMediaUrl(url);
  const supported =
    CLOUDINARY_UPLOAD_RE.test(absolute) || isUnsplashUrl(absolute);
  if (!supported) return "";
  return widths
    .map((w) => `${responsiveVariant(absolute, w)} ${w}w`)
    .join(", ");
};
